/**
 * ThemeProvider — controls the active design-system theme.
 *
 * Three themes are wired in `index.css`:
 *   - "executive" (DEFAULT): Executive Ledger — cream canvas, dark sidebar,
 *                            burnt-orange CTAs, Newsreader serif.
 *   - "editorial":           AlphaJournal Editorial — cream + Playfair Display + gold.
 *   - "pro":                 Behavioral Intelligence System — dark FinTech, electric violet.
 *
 * The provider toggles `data-theme` on <html> on every theme change. It only
 * persists to localStorage when the user *explicitly* picks a theme via
 * `setTheme()` or `cycleTheme()` — never on a silent default. This means
 * flipping `DEFAULT_THEME` in code is immediately visible to everyone who
 * has not yet picked a theme via the UI; without this distinction, the
 * first-visit default gets frozen into localStorage and overrides any
 * future default change.
 *
 * Usage:
 *   const { theme, setTheme, cycleTheme } = useTheme();
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "executive" | "editorial" | "pro";

const THEME_ORDER: ReadonlyArray<Theme> = ["executive", "editorial", "pro"];

// Bumped to "v3" to clear stale silent-default writes from the v2 era
// (when editorial briefly held the default). The persistence logic below
// no longer writes silent defaults, so future default flips will not
// require another key bump.
const STORAGE_KEY = "tj.theme.v3";
const DEFAULT_THEME: Theme = "executive";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Cycle through executive → editorial → pro → executive. */
  cycleTheme: () => void;
  /** Legacy alias for cycleTheme — kept for any callers that already use it. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function isTheme(value: string | null): value is Theme {
  return value === "executive" || value === "editorial" || value === "pro";
}

function readStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage may be blocked (private mode, quota); ignore.
  }
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme() ?? DEFAULT_THEME);

  // Apply the current theme to <html> on mount and whenever it changes.
  // Persistence is deliberately NOT done here — see file header.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStoredTheme(next);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((current) => {
      const idx = THEME_ORDER.indexOf(current);
      const next = THEME_ORDER[(idx + 1) % THEME_ORDER.length] ?? DEFAULT_THEME;
      writeStoredTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      cycleTheme,
      toggleTheme: cycleTheme,
    }),
    [theme, setTheme, cycleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

const FALLBACK_VALUE: ThemeContextValue = {
  theme: DEFAULT_THEME,
  setTheme: () => {},
  cycleTheme: () => {},
  toggleTheme: () => {},
};

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? FALLBACK_VALUE;
}
